import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { updateSuiteRun } from '@/features/executions/data/suiteRuns';
import { buildSuiteRunParentInvalidations } from '@/features/executions/queries/executionInvalidationFilters';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { patchQueryResultCaches } from '@/shared/data/queryResultCache';
import {
  gridPaths,
  gridQueryKey,
  invalidateGridQuery,
  invalidateFeatures,
  invalidateReleases,
  invalidateSuites,
  invalidateSuiteRunDetail,
  invalidateSuiteRuns,
  invalidateTestRuns,
  queryKeys,
  useInvalidateQueries,
} from '@/shared/hooks';
import type { TagColor } from '@/shared/data/tags';
import type { SuiteRunDto } from '@flowqa/shared';

type SuiteRunEditSaveInput = {
  colors: TagColor[];
  durationMs?: number | null;
  status?: SuiteRunDto['status'];
  tags: string[];
  timestamp?: Date | null;
};

type SuiteRunEditDialogState = {
  editLoading: boolean;
  editTarget: SuiteRunDto | null;
  onEditClose: () => void;
  onEditRow: (row: SuiteRunDto) => void;
  onEditSave: (input: SuiteRunEditSaveInput) => Promise<void>;
};

export const useSuiteRunEditDialog = (
  selectedProjectId: string | null,
): SuiteRunEditDialogState => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const [editTarget, setEditTarget] = useState<SuiteRunDto | null>(null);

  const editEditor = useDetailEditEditor<SuiteRunDto['status']>({
    projectId: editTarget?.projectId ?? null,
    successMessage: 'Suite run updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      if (!editTarget) return;
      const organisationId = editTarget.organisationId ?? selectedOrganisationId ?? null;
      const updated = await updateSuiteRun(
        { id: editTarget.id, tags, status, timestamp, durationMs },
        organisationId,
      );
      patchQueryResultCaches(
        queryClient,
        [
          { queryKey: queryKeys.suiteRuns(selectedProjectId, organisationId) },
          { queryKey: gridQueryKey(gridPaths.suiteRuns) },
        ],
        updated,
      );
      await invalidateQueries([
        ...buildSuiteRunParentInvalidations(editTarget),
        invalidateSuiteRuns(selectedProjectId, organisationId),
        invalidateSuiteRunDetail(editTarget.id, organisationId),
        invalidateSuites(selectedProjectId, organisationId),
        invalidateFeatures(selectedProjectId, organisationId),
        invalidateReleases(selectedProjectId, organisationId),
        invalidateTestRuns(selectedProjectId, organisationId),
        invalidateGridQuery(gridPaths.suiteRuns),
        invalidateGridQuery(gridPaths.testRuns),
        invalidateGridQuery(gridPaths.suites),
        invalidateGridQuery(gridPaths.features),
        invalidateGridQuery(gridPaths.releases),
      ]);
    },
  });

  const onEditClose = useCallback((): void => {
    if (editEditor.isPending) return;
    setEditTarget(null);
  }, [editEditor.isPending]);

  const onEditSave = useCallback(
    async ({
      tags,
      colors,
      status,
      timestamp,
      durationMs,
    }: SuiteRunEditSaveInput): Promise<void> => {
      await editEditor.save({ tags, colors, status, timestamp, durationMs });
      setEditTarget(null);
    },
    [editEditor],
  );

  return {
    editLoading: editEditor.isPending,
    editTarget,
    onEditClose,
    onEditRow: setEditTarget,
    onEditSave,
  };
};
